import { useEffect } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, ExternalLink, ArrowUp, Check, CircleSlash, Minus } from "lucide-react";
import { Link } from "react-router";
import { FreelancerListItem } from "@/types/freelancer";
import { Vaga } from "@/types/vaga";
import { FreelancerScoreBadge } from "./FreelancerScoreBadge";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";
import { formatLocation, getInitials } from "./freelancerCardShared";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { DisponibilidadeLinha } from "@/components/freelancers/DisponibilidadeBadge";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";
import { cn } from "@/lib/utils";
import { rankExperiencia } from "@/utils/freelancer-experiencia";
import {
  matchAreas,
  matchLocalizacao,
  matchVerba,
  type MatchResult,
  type MatchStatus,
} from "@/utils/vaga-match";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FreelancerCompareDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  freelancers: FreelancerListItem[];
  onRemove: (id: string) => void;
  /** Quando definida, adiciona linhas "Match com a vaga" no topo da tabela. */
  vaga?: Vaga | null;
}

interface Row {
  key: string;
  label: string;
  value: (f: FreelancerListItem) => React.ReactNode;
  /** Quando true, destaca linhas com valores divergentes em amber. */
  highlightDivergences?: boolean;
  /** Como extrair o valor primitivo para detectar divergência (campos categóricos). */
  raw?: (f: FreelancerListItem) => string | number | null | undefined;
  /** Quando definido, mostra barrinha + chip "↑ Melhor" no(s) vencedor(es) da linha. */
  comparable?: {
    /** Valor numérico para ranking. Retorna null se não houver dado. */
    getValue: (f: FreelancerListItem) => number | null;
    /** Direção de "melhor". Hoje só "higher" — "lower" reservado para uso futuro. */
    better?: "higher";
  };
}

const ROWS: Row[] = [
  {
    key: "score",
    label: "Score de reputação",
    value: (f) => <FreelancerScoreBadge freelancer={f} size="sm" />,
    comparable: { getValue: (f) => f.score_reputacao ?? null },
  },
  {
    key: "media_perfil",
    label: "Média perfil",
    value: (f) =>
      typeof f.media_avaliacao_perfil === "number"
        ? `${f.media_avaliacao_perfil.toFixed(1)} (${f.total_avaliacoes_perfil ?? 0})`
        : "—",
    comparable: { getValue: (f) => f.media_avaliacao_perfil ?? null },
  },
  {
    key: "media_projeto",
    label: "Média projeto",
    value: (f) =>
      typeof f.media_avaliacao_projeto === "number"
        ? `${f.media_avaliacao_projeto.toFixed(1)} (${f.total_avaliacoes_projeto ?? 0})`
        : "—",
    comparable: { getValue: (f) => f.media_avaliacao_projeto ?? null },
  },
  {
    key: "aprovacoes",
    label: "Aprovações em vagas",
    value: (f) => f.total_aprovacoes ?? 0,
    comparable: { getValue: (f) => f.total_aprovacoes ?? 0 },
  },
  {
    key: "shortlists",
    label: "Vezes em shortlist",
    value: (f) => f.total_shortlists ?? 0,
    comparable: { getValue: (f) => f.total_shortlists ?? 0 },
  },
  {
    key: "projetos",
    label: "Projetos concluídos",
    value: (f) => f.total_projetos_concluidos ?? 0,
    comparable: { getValue: (f) => f.total_projetos_concluidos ?? 0 },
  },
  {
    key: "conversao_shortlist",
    label: "Taxa de conversão (passou pela shortlist)",
    value: (f) =>
      typeof f.taxa_conversao_shortlist === "number"
        ? `${Math.round(f.taxa_conversao_shortlist)}%`
        : "—",
    comparable: { getValue: (f) => f.taxa_conversao_shortlist ?? null },
  },
  {
    key: "atributos",
    label: "Top atributos",
    value: (f) => {
      const list = (f.atributos_medias ?? [])
        .filter((a) => a.total > 0)
        .sort((a, b) => b.media - a.media)
        .slice(0, 4);
      if (list.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="flex flex-col gap-1">
          {list.map((a) => (
            <div key={a.slug} className="flex items-center gap-2 text-xs">
              <span className="capitalize w-20 truncate">{a.nome.toLowerCase()}</span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500"
                  style={{ width: `${Math.min(100, (a.media / 10) * 100)}%` }}
                />
              </div>
              <span className="font-mono font-semibold w-8 text-right">
                {a.media.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    key: "areas",
    label: "Áreas",
    value: (f) => f.areas?.join(", ") ?? "—",
    raw: (f) => (f.areas ?? []).slice().sort().join("|"),
    highlightDivergences: true,
  },
  {
    key: "local",
    label: "Localização",
    value: (f) => formatLocation(f) ?? "—",
    raw: (f) => formatLocation(f) ?? "",
    highlightDivergences: true,
  },
  {
    key: "pretensao",
    label: "Pretensão",
    value: (f) => f.pretensao_salarial ?? "—",
    raw: (f) => f.pretensao_salarial ?? "",
    highlightDivergences: true,
  },
  {
    key: "disponibilidade",
    label: "Disponibilidade",
    // Sobrescrito no componente pelo DisponibilidadeBadge unificado (precisa do
    // hook useDisponibilidadeBatch). O badge não tem valor primitivo comparável,
    // então esta linha não entra na detecção de divergência.
    value: () => null,
  },
  {
    key: "idiomas",
    label: "Idiomas",
    value: (f) => f.idiomas?.join(", ") ?? "—",
  },
  {
    key: "tempo",
    label: "Experiência",
    value: (f) => f.tempo_experiencia ?? "—",
    comparable: { getValue: (f) => rankExperiencia(f.tempo_experiencia) },
  },
  {
    key: "selo",
    label: "Selo",
    value: (f) =>
      f.quality_seal ? (
        <Badge className="bg-blue-100 text-blue-800 border-blue-300">Verificado</Badge>
      ) : (
        <span className="text-muted-foreground text-xs">Não verificado</span>
      ),
    comparable: { getValue: (f) => (f.quality_seal ? 1 : 0) },
  },
];

function valuesDiverge(values: (string | number | null | undefined)[]): boolean {
  const seen = new Set<string>();
  for (const v of values) {
    seen.add(v === undefined || v === null ? "__null__" : String(v));
    if (seen.size > 1) return true;
  }
  return false;
}

function formatDelta(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

const MATCH_STYLES: Record<MatchStatus, { className: string; icon: typeof Check; label: string }> = {
  match: {
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: Check,
    label: "Match",
  },
  parcial: {
    className: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Minus,
    label: "Parcial",
  },
  mismatch: {
    className: "bg-rose-50 text-rose-700 border-rose-200",
    icon: CircleSlash,
    label: "Não bate",
  },
  sem_dado: {
    className: "bg-slate-50 text-slate-500 border-slate-200",
    icon: Minus,
    label: "Sem dado",
  },
};

function MatchChip({ result }: { result: MatchResult }) {
  const style = MATCH_STYLES[result.status];
  const Icon = style.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
            style.className,
          )}
        >
          <Icon className="h-3 w-3" />
          {style.label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-xs">{result.detalhe}</TooltipContent>
    </Tooltip>
  );
}

function CompareIndicator({
  values,
  currentIndex,
}: {
  values: (number | null)[];
  currentIndex: number;
}) {
  const valid = values.filter((v): v is number => v != null);
  if (valid.length < 2) return null; // só 1 freelancer ou só 1 com dado
  const max = Math.max(...valid);
  if (max === 0) return null; // ninguém tem o sinal
  const me = values[currentIndex];
  if (me == null) return null;
  const isWinner = me === max;
  const second = [...valid].sort((a, b) => b - a).find((v) => v < max) ?? max;
  const delta = isWinner && second < max ? max - second : null;
  const widthPct = Math.max(2, (me / max) * 100);
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            isWinner ? "bg-emerald-500" : "bg-slate-300",
          )}
          style={{ width: `${widthPct}%` }}
        />
      </div>
      {isWinner && (
        <span className="inline-flex items-center gap-0.5 text-emerald-700 text-[10px] font-semibold leading-none">
          <ArrowUp className="h-3 w-3" />
          {delta != null && delta > 0 ? `+${formatDelta(delta)}` : null}
        </span>
      )}
    </div>
  );
}

export function FreelancerCompareDrawer({
  open,
  onOpenChange,
  freelancers,
  onRemove,
  vaga,
}: FreelancerCompareDrawerProps) {
  // Disponibilidade unificada (v_availability_summary) em lote p/ os comparados.
  const { data: dispMap } = useDisponibilidadeBatch(freelancers.map((f) => f.id));

  // Linhas de match com a vaga, apenas se a vaga foi passada e tem algum requisito.
  const matchRows: Row[] = vaga
    ? [
        {
          key: "match_areas",
          label: "Match: áreas",
          value: (f) => <MatchChip result={matchAreas(vaga, f)} />,
        },
        {
          key: "match_local",
          label: "Match: localização",
          value: (f) => <MatchChip result={matchLocalizacao(vaga, f)} />,
        },
        {
          key: "match_verba",
          label: "Match: verba",
          value: (f) => <MatchChip result={matchVerba(vaga, f)} />,
        },
      ]
    : [];
  // Injeta o DisponibilidadeBadge na linha de disponibilidade (precisa do dispMap
  // do hook, por isso não pode viver na const ROWS de módulo).
  const rows = ROWS.map((row) =>
    row.key === "disponibilidade"
      ? {
          ...row,
          value: (f: FreelancerListItem) => (
            <DisponibilidadeLinha summary={dispMap?.get(f.id)} level="inline" />
          ),
        }
      : row,
  );
  const allRows = [...matchRows, ...rows];

  // Registra visualização para cada freelancer comparado quando o drawer abre.
  useEffect(() => {
    if (!open) return;
    freelancers.forEach((f) => trackFreelancerView(f.id, "comparar"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, freelancers.map((f) => f.id).join(",")]);

  if (freelancers.length === 0) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none sm:w-[min(100vw,calc(280px*var(--cols)+120px))] p-0 flex flex-col"
        style={{ ["--cols" as unknown as string]: String(freelancers.length) }}
      >
        <TooltipProvider>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Comparar {freelancers.length} freelancer{freelancers.length > 1 ? "s" : ""}
          </h2>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background border-b z-10">
              <tr>
                <th className="text-left px-3 py-3 w-[160px] font-medium text-muted-foreground align-bottom">
                  Critério
                </th>
                {freelancers.map((f) => (
                  <th
                    key={f.id}
                    className="text-left px-3 py-3 align-bottom min-w-[260px]"
                  >
                    <div className="flex items-start gap-2">
                      <div className="size-9 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-xs shrink-0">
                        {getInitials(f.nome)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/freelancers/${f.id}`}
                          className="text-left w-full hover:underline block"
                        >
                          <div className="font-semibold truncate">{f.nome}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {f.email}
                          </div>
                        </Link>
                        <div className="flex items-center gap-0.5 mt-1">
                          <ExternalLinkButton
                            kind="linkedin"
                            url={f.linkedin_url}
                            className="h-6 w-6"
                            iconClassName="h-3 w-3"
                          />
                          <ExternalLinkButton
                            kind="portfolio"
                            url={f.portfolio_url}
                            className="h-6 w-6"
                            iconClassName="h-3 w-3"
                          />
                          <Button asChild variant="ghost" size="icon" className="h-6 w-6">
                            <Link to={`/freelancers/${f.id}`} title="Abrir detalhes">
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                      {freelancers.length > 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => onRemove(f.id)}
                          aria-label="Remover da comparação"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allRows.map((row) => {
                const rawValues = row.raw ? freelancers.map(row.raw) : [];
                const diverges =
                  row.highlightDivergences && row.raw && valuesDiverge(rawValues);
                const compareValues = row.comparable
                  ? freelancers.map((f) => row.comparable!.getValue(f))
                  : null;
                return (
                  <tr
                    key={row.key}
                    className={cn("border-b align-top", diverges && "bg-amber-50/60")}
                  >
                    <td className="px-3 py-3 text-muted-foreground font-medium text-xs">
                      {row.label}
                    </td>
                    {freelancers.map((f, idx) => (
                      <td key={f.id} className="px-3 py-3">
                        {row.value(f)}
                        {compareValues && (
                          <CompareIndicator
                            values={compareValues}
                            currentIndex={idx}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </TooltipProvider>
      </SheetContent>
    </Sheet>
  );
}
